// 11 §5 — the intent layer. One renderer per `ProjectedEffect` kind (§5.2), future HP bars and
// kill marks (§5.3), owner × state styles (§5.4), the grey declaration layer and the clutter
// policy (§5.5). Everything is drawn in overlay-camera space with Graphics/Text/Images so
// strokes stay one physical pixel at every board zoom; positions are `world × zoom` inside
// containers that the scene offsets by `−scroll × zoom`. Redraws diff by effect id.
//
// No number here is computed: every figure comes from the ProjectedBoard (18.2 `intentlayer.nonumbers`).
import Phaser from 'phaser';
import type {
  AimRange, AreaHit, ConflictFlag, EnemyDeclaration, IntentRef, OrderingNote, ProjectedBoard, ProjectedEffect, ProjectedUnit,
  Snapshot, Survey, Tile, UnitState,
} from '../../core/sim/types';
import { fieldRoute, fieldTileNote } from '../../core/sim/field';
import { effectId, intentKey, neverSeen, tileEq } from '../../core/sim/types';
import { bodyStops, originFor, sizeOf, tilesAt } from '../../core/sim/body';
import type { Filter } from '../../core/stores/view';
import type { RouteTag } from '../../core/stores/authoring';
import type { ColourMode } from '../../core/stores/settings';
import type { AtlasManifest } from '../../config';
import { unitFrame } from '../assets';
import { shoveIntoWater, waterAt } from '../../core/sim/water';
import { PALETTES, SHAPE_MARKS, STATUS_ABBR, attackStroke, hex, type IntentPalette } from './palette';
import { slotMark } from '../../ui/encounter/slots';
import { seatColour } from '../board/players';
import { pixelRatio } from '../board/BoardCamera';
import type { Suggestion } from '../../core/authoring/stateMachine';
import { bodyOriginY, bodyScale } from '../hugeArt';

/** docs/103: a flanked blow's gold (the preview's pincer and call-out, the playback's sparks). */
const FLANK_GOLD = 0xffd34d;

export interface IntentView {
  zoom: number; cell: number;
  filter: Filter; hidePaths: boolean; ownerHighlight?: string; hoverHero?: number; humans: number;
  me: string; owners: Record<number, string>; colourMode: ColourMode; textScale: number; highlightDanger: boolean;
  selectedHero?: number; cursor?: Tile; dashPhase: number;
  /** owner hex → seat (game/board/players): my plan draws in my player colour, teammates' ghosts at 55 %. */
  seats?: Map<string, number>;
  /**
   * While an action is chosen: the tiles it may aim at, tinted by kind (mockup 05). `quiet`: shown
   * with no action chosen — the selected hero's walk (docs/33 E2) or a hovered hotbar tile (docs/48 AR4).
   * docs/48: `aim` is the action's whole range from `from`, where the hero will stand when it acts;
   * `targets` counts the units in `tiles` for unit-aimed actions.
   */
  reach?: { tiles: Tile[]; tone: 'move' | 'attack' | 'area' | 'support'; field?: Survey; quiet?: boolean; aim?: AimRange; from?: Tile; targets?: number };
  /** docs/33 E1: what a click at the cursor would do, with no action chosen. */
  suggest?: Suggestion;
  /** docs/149 MP: the walks under way, each tagged with the pulse it reaches every square in. */
  routes?: RouteTag[];
  /** docs/33 E2: why the chosen action cannot aim at the cursor, in words. */
  note?: string;
  /**
   * docs/63: the cursor is on a square the party has never seen: no words about it. docs/139 WD5:
   * a walk's route still runs there when the walk (judged as on open floor) reaches it.
   */
  dark?: boolean;
}

export interface IntentLayers {
  declarations: Phaser.GameObjects.Container; intent: Phaser.GameObjects.Container; hud: Phaser.GameObjects.Container;
  /** A board-camera layer under the units: the selection and cursor squares, in world px, so a figure stands over them. */
  ground: Phaser.GameObjects.Layer;
}

interface Node { sig: string; obj: Phaser.GameObjects.Container }

interface Style { alpha: number; badge: 'hollow' | 'filled' | 'lock'; ghost: 'pulse' | 'dashed' | 'solid'; struck: boolean; mine: boolean }

const FONT = '"Alegreya Sans", ui-sans-serif, system-ui, "Segoe UI", Roboto, sans-serif';
/** Mockup 05 tile overlays: move reach, attack range (warm red since docs/48: the mockup's olive went muddy on night boards), area template (fill, edge). */
/** docs/93 AQ8: a shove into the deep and the square it would flounder on. */
const DEEP_MARK = 0x4fb4ff;
const REACH: Record<'move' | 'attack' | 'area' | 'support', [number, number]> = { move: [0x5aa9ff, 0x8cc4ff], attack: [0xff7650, 0xffa488], area: [0xa58ad0, 0xc9b2f0], support: [0x4ccf6a, 0x8be29e] };

export class IntentLayer {
  private nodes = { declarations: new Map<string, Node>(), intent: new Map<string, Node>(), hud: new Map<string, Node>(), ground: new Map<string, Node>() };
  private pal: IntentPalette = PALETTES.default;
  private view!: IntentView;
  private units = new Map<number, ProjectedUnit>();
  private states = new Map<number, UnitState>();
  /** docs/93 AQ8: the board drawn under the plans (where the deep is). */
  private board?: Snapshot['board'];
  /** docs/139 WD5: the party's fog, so a walk's never-seen squares read as a hope, not a fact. */
  private fog?: Snapshot['fog'];

  constructor(private readonly scene: Phaser.Scene, private readonly layers: IntentLayers, private readonly manifest: AtlasManifest) {}

  // ---- public ---------------------------------------------------------------------------

  redraw(board: ProjectedBoard, snapshot: Snapshot, view: IntentView): void {
    this.view = view;
    this.pal = PALETTES[view.colourMode];
    this.units = new Map(board.units.map((u) => [u.unitId, u]));
    this.states = new Map(snapshot.units.map((u) => [u.unitId, u]));
    this.board = snapshot.board;
    this.fog = snapshot.fog;
    const envSig = `${view.zoom}|${view.colourMode}|${view.textScale}|${view.filter}|${view.hidePaths}|${view.ownerHighlight ?? ''}|${view.hoverHero ?? ''}|${view.humans}`;

    // Declarations (grey layer)
    const wantDecl = new Map<string, { sig: string; build: () => Phaser.GameObjects.Container }>();
    // On the ground under the units, so a figure stands over them: the reach washes, the danger hatching, the
    // selection and cursor squares (built in overlay px like the rest, scaled into board px by `sync`)
    const wantGround = new Map<string, { sig: string; build: () => Phaser.GameObjects.Container }>();
    if (view.reach?.field) {
      // docs/26: a Move's reach comes from the field survey — cost-shaded, zones of control, and
      // the A* route to the cursor, the very route the pulse will walk
      const f = view.reach.field;
      const fsig = `${envSig}|${f.unit}|${f.budget}|${f.from.x},${f.from.y}|${f.reach.map((r) => `${r.tile.x},${r.tile.y},${r.cost}`).join(';')}|${f.zones.length}`;
      const quiet = !!view.reach.quiet;
      wantGround.set('reach', { sig: `${fsig}|${quiet}`, build: () => this.buildField(f, quiet) });
      // with no action chosen the route runs to where the click would walk (docs/33 E1), if anywhere
      const to = quiet ? (view.suggest?.kind === 'plan' ? view.suggest.walkTo : undefined) : view.cursor;
      if (to) wantDecl.set('route', { sig: `${fsig}|${to.x},${to.y}|${quiet}`, build: () => this.buildRoute(f, to, quiet && !!view.suggest && view.suggest.kind === 'plan' && view.suggest.tone !== 'move', quiet) });
    } else if (view.reach?.aim) { const r = view.reach, aim = r.aim!; wantGround.set('reach', { sig: envSig + JSON.stringify(r) + JSON.stringify(view.selectedHero), build: () => this.buildAim(aim, r.tiles, r.tone, !!r.quiet, r.targets !== undefined) }); }
    else if (view.reach && view.reach.tiles.length) { const r = view.reach; wantGround.set('reach', { sig: envSig + JSON.stringify(r), build: () => this.buildReach(r.tiles, r.tone) }); }
    for (const d of board.declarations) wantDecl.set(`decl:${d.actor}`, { sig: envSig + JSON.stringify(d), build: () => this.buildDeclaration(d) });
    for (const r of view.routes ?? []) wantGround.set(`walk:${r.heroId}`, { sig: envSig + JSON.stringify(r.legs), build: () => this.buildWalkTags(r) });
    this.sync('declarations', wantDecl);

    // Intent glyphs, conflicts, ordering
    const want = new Map<string, { sig: string; build: () => Phaser.GameObjects.Container }>();
    const byKey = new Map(board.effects.map((e) => [intentKey(e.id), e]));
    const visibleTargets = new Set<number>();
    for (const e of board.effects) {
      if (!this.visible(e.id, e.kind)) continue;
      const style = this.styleFor(e.id);
      const flags = board.conflicts.filter((c) => intentKey(c.intent) === intentKey(e.id));
      want.set(effectId(e), { sig: envSig + JSON.stringify([e, style, flags]), build: () => this.buildEffect(e, style, flags) });
      if (e.kind === 'attack') visibleTargets.add(e.target);
      if (e.kind === 'area') for (const h of e.hits) visibleTargets.add(h.target);
    }
    board.ordering.forEach((o, i) => {
      const then = byKey.get(intentKey(o.then));
      if (!then || !this.visible(o.then, then.kind)) return;
      want.set(`order:${i}`, { sig: envSig + JSON.stringify(o), build: () => this.buildOrdering(o, then, i) });
    });
    this.sync('intent', want);

    // HUD: HP bars as they stand, target rings, status badges, selection ring, cursor, danger hatching
    const wantHud = new Map<string, { sig: string; build: () => Phaser.GameObjects.Container }>();
    for (const u of board.units) {
      // docs/50: the fallen are scenery — no HP plate, badges or ring
      if (this.states.get(u.unitId)?.onBoard === false) continue;
      const targeted = visibleTargets.has(u.unitId);
      wantHud.set(`hp:${u.unitId}`, { sig: envSig + JSON.stringify([u, targeted, this.states.get(u.unitId)?.faction]), build: () => this.buildUnitHud(u, targeted) });
    }
    if (view.highlightDanger) {
      const danger = this.dangerTiles(board.declarations);
      if (danger.length) wantGround.set('danger', { sig: envSig + JSON.stringify(danger), build: () => this.buildDanger(danger) });
    }
    if (view.selectedHero !== undefined) {
      const u = board.units.find((x) => x.heroId === view.selectedHero);
      if (u) wantGround.set('selection', { sig: envSig + JSON.stringify(u.start), build: () => this.buildSelection(u.start, this.sizeOfUnit(u.unitId)) });
    }
    if (view.cursor) {
      // docs/79 HU5: pointing where a huge hero would walk outlines its whole body there
      const mover = board.units.find((x) => x.heroId === view.selectedHero);
      const n = this.sizeOfUnit(mover?.unitId);
      const walk = view.suggest?.kind === 'plan' && view.suggest.tone === 'move' ? view.suggest.walkTo : undefined;
      const [at, side] = n > 1 && walk ? [walk, n] : [view.cursor, 1];
      wantGround.set('cursor', { sig: envSig + JSON.stringify(at) + side, build: () => this.buildCursor(at, side) });
    }
    if (view.cursor && (view.suggest || view.note)) {
      const cur = view.cursor, sug = view.suggest, note = view.note;
      wantHud.set('suggest', { sig: envSig + JSON.stringify([cur, sug?.kind, sug?.label, sug?.kind === 'plan' ? [sug.target, sug.hit, sug.about] : undefined, note]), build: () => this.buildSuggest(cur, sug, note) });
    }
    this.sync('hud', wantHud);
    this.sync('ground', wantGround);
    this.declutter();
  }

  /**
   * docs/33 E7: word chips never sit on one another. Each chip goes back to where it was drawn, then,
   * cursor and my own first, any chip that would overlap one already placed climbs above it.
   */
  private declutter(): void {
    type Box = { c: Phaser.GameObjects.Container; x: number; y: number; w: number; h: number };
    const boxes: Array<Box & { pri: number }> = [];
    const visit = (o: Phaser.GameObjects.Container, ox: number, oy: number, pri: number) => {
      for (const ch of o.list) {
        if (!(ch instanceof Phaser.GameObjects.Container)) continue;
        if (ch.getData('chip')) {
          const home = (ch.getData('home') as { x: number; y: number } | undefined) ?? { x: ch.x, y: ch.y };
          ch.setData('home', home).setPosition(home.x, home.y);
          boxes.push({ c: ch, x: ox + home.x - ch.width / 2, y: oy + home.y - ch.height / 2, w: ch.width, h: ch.height, pri });
        } else visit(ch, ox + ch.x, oy + ch.y, pri);
      }
    };
    visit(this.layers.hud, 0, 0, 0); visit(this.layers.intent, 0, 0, 1); visit(this.layers.declarations, 0, 0, 2);
    boxes.sort((a, b) => a.pri - b.pri || b.y - a.y || a.x - b.x);
    const placed: Box[] = [];
    const hit = (b: Box) => placed.find((p) => b.x < p.x + p.w + 1 && p.x < b.x + b.w + 1 && b.y < p.y + p.h + 1 && p.y < b.y + b.h + 1);
    for (const b of boxes) {
      for (let i = 0, o = hit(b); o && i < 8; i++, o = hit(b)) { const dy = b.y - (o.y - b.h - 2); b.y -= dy; b.c.y -= dy; }
      placed.push(b);
    }
  }

  /** Drop every drawn node (e.g. once web fonts load) so the next redraw rebuilds them. */
  reset(): void { for (const m of Object.values(this.nodes)) { for (const n of m.values()) n.obj.destroy(); m.clear(); } }

  /** Slow dash-phase animation (§5.2 "dash phase animated slowly") without rebuilding nodes. */
  tick(dashPhase: number): void {
    for (const n of this.nodes.intent.values()) {
      const g = n.obj.getData('dash') as Phaser.GameObjects.Graphics | undefined;
      const redraw = n.obj.getData('dashRedraw') as ((phase: number) => void) | undefined;
      if (g && redraw) redraw(dashPhase);
    }
  }

  /**
   * While a pulse plays out (or the board has not caught up with the next one) the plan is hidden:
   * the ground keeps only the cursor, under the pointer, so it does not stick where the turn ended.
   */
  quietCursor(cursor: Tile | undefined, zoom: number, cell: number): void {
    if (!this.view) return;
    this.view = { ...this.view, zoom, cell };
    const want = new Map<string, { sig: string; build: () => Phaser.GameObjects.Container }>();
    if (cursor) want.set('cursor', { sig: `quiet|${zoom}|${cell}|${cursor.x},${cursor.y}`, build: () => this.buildCursor(cursor) });
    this.sync('ground', want);
  }

  // ---- diff -------------------------------------------------------------------------------

  private sync(layer: keyof IntentLayers, want: Map<string, { sig: string; build: () => Phaser.GameObjects.Container }>): void {
    const have = this.nodes[layer];
    for (const [id, n] of have) if (!want.has(id)) { n.obj.destroy(); have.delete(id); }
    for (const [id, w] of want) {
      const n = have.get(id);
      if (n && n.sig === w.sig) continue;
      n?.obj.destroy();
      const obj = w.build();
      // the ground layer is in board px, under the board camera's zoom
      if (layer === 'ground') obj.setScale(1 / this.view.zoom);
      this.layers[layer].add(obj);
      have.set(id, { sig: w.sig, obj });
    }
  }

  // ---- policy (§5.4, §5.5) ------------------------------------------------------------------

  private isMine(ref: IntentRef): boolean { return this.view.owners[ref.heroId] === this.view.me; }
  /** The owning player's colour (seat order), for paths and ghosts. */
  private seatOf(ref: IntentRef): number { const o = this.view.owners[ref.heroId]; return seatColour(o ? this.view.seats?.get(o) : undefined); }

  private visible(ref: IntentRef, kind: ProjectedEffect['kind']): boolean {
    const v = this.view;
    if (v.hoverHero !== undefined && ref.heroId === v.hoverHero) return true;
    switch (v.filter) {
      case 'mine': case 'wing': return this.isMine(ref);
      case 'attacks': return kind === 'attack' || kind === 'area';
      case 'all': return true;
    }
  }

  private styleFor(ref: IntentRef): Style {
    const v = this.view;
    const mine = this.isMine(ref);
    let s: Style;
    if (ref.state === 'rejected') s = { alpha: 1, badge: 'hollow', ghost: 'solid', struck: true, mine };
    else if (mine) s = ref.state === 'local' ? { alpha: 1, badge: 'hollow', ghost: 'pulse', struck: false, mine }
      : ref.state === 'committed' ? { alpha: 1, badge: 'lock', ghost: 'solid', struck: false, mine }
      : { alpha: 1, badge: 'filled', ghost: 'solid', struck: false, mine };
    else s = ref.state === 'committed' ? { alpha: 0.85, badge: 'lock', ghost: 'solid', struck: false, mine }
      : { alpha: 0.55, badge: 'hollow', ghost: 'dashed', struck: false, mine };
    if (!mine && v.humans >= 5 && v.hoverHero !== ref.heroId) s.alpha = Math.min(s.alpha, 0.7);
    if (v.ownerHighlight) s.alpha = v.owners[ref.heroId] === v.ownerHighlight ? 1 : 0.3;
    if (v.hoverHero !== undefined) s.alpha = ref.heroId === v.hoverHero ? 1 : Math.min(s.alpha, 0.35);
    return s;
  }

  private hidePathFor(ref: IntentRef): boolean {
    const v = this.view;
    if (v.hoverHero === ref.heroId) return false;
    return (v.hidePaths || v.humans >= 10) && !this.isMine(ref);
  }

  // ---- geometry helpers ----------------------------------------------------------------------

  private get px(): number { return this.view.cell * this.view.zoom; }
  private origin(t: Tile) { return { x: t.x * this.px, y: t.y * this.px }; }
  private centre(t: Tile) { return { x: t.x * this.px + this.px / 2, y: t.y * this.px + this.px / 2 }; }
  private pivot(t: Tile, n = 1) { return { x: (t.x + n / 2) * this.px, y: (t.y + n) * this.px }; }
  /** docs/79: a body's side (1 for one square), and the centre of its footprint from origin `t`. */
  private sizeOfUnit(unitId: number | undefined): number { return unitId === undefined ? 1 : sizeOf(this.states.get(unitId) ?? {}); }
  private mid(t: Tile, n = 1) { return { x: (t.x + n / 2) * this.px, y: (t.y + n / 2) * this.px }; }
  /** Every square the units cover where they stand now (a huge body's whole footprint). */
  private occupiedSet(): Set<string> {
    const out = new Set<string>();
    for (const u of this.units.values()) if (u.onBoardEnd !== false) for (const t of tilesAt(u.start, this.sizeOfUnit(u.unitId))) out.add(`${t.x},${t.y}`);
    return out;
  }
  /** Four corner brackets round a footprint (`n` squares a side), a dark keyline under the colour. */
  private brackets(g: Phaser.GameObjects.Graphics, t: Tile, n: number, col: number, out = 0, arm?: number, widths: ReadonlyArray<readonly [number, number, number]> = [[3, 0x0b0f1a, 0.8], [1, col, 1]]): void {
    const o = this.origin(t), side = this.px * n;
    const x0 = o.x - out, y0 = o.y - out, x1 = o.x + side + out, y1 = o.y + side + out;
    const k = arm ?? Math.max(4, Math.round(this.px / 4));
    for (const [w, colour, alpha] of widths) {
      g.lineStyle(w, colour, alpha);
      g.beginPath();
      g.moveTo(x0, y0 + k); g.lineTo(x0, y0); g.lineTo(x0 + k, y0);
      g.moveTo(x1 - k, y0); g.lineTo(x1, y0); g.lineTo(x1, y0 + k);
      g.moveTo(x1, y1 - k); g.lineTo(x1, y1); g.lineTo(x1 - k, y1);
      g.moveTo(x0 + k, y1); g.lineTo(x0, y1); g.lineTo(x0, y1 - k);
      g.strokePath();
    }
  }
  private endTile(unitId: number): Tile { return this.units.get(unitId)?.end ?? { x: 0, y: 0 }; }
  private startTile(unitId: number): Tile { return this.units.get(unitId)?.start ?? { x: 0, y: 0 }; }
  /**
   * docs/81 SA2: where the hero stands as this slot acts — after its own earlier slots walk it
   * (a Search after a Move is marked on the Move's ghost) — as the sim says; else where it stands now.
   */
  private actsFrom(e: { id: IntentRef; from?: Tile }): Tile {
    if (e.from) return e.from;
    const u = this.heroUnit(e.id.heroId);
    return u ? this.startTile(u.unitId) : { x: 0, y: 0 };
  }
  /** Where a support's `i`th beneficiary stands as it is helped (not where it ends the pulse). */
  private helpedAt(e: Extract<ProjectedEffect, { kind: 'support' }>, i: number): Tile { return e.at?.[i] ?? this.endTile(e.beneficiaries[i]!); }
  private fontPx(base: number): number { return Math.round(base * this.view.textScale); }

  private text(x: number, y: number, str: string, opts: { size?: number; color?: string; bold?: boolean; origin?: [number, number] } = {}): Phaser.GameObjects.Text {
    const t = this.scene.add.text(x, y, str, {
      fontFamily: FONT, fontSize: `${this.fontPx(opts.size ?? 11)}px`, color: opts.color ?? this.pal.text, fontStyle: opts.bold ? 'bold' : 'normal',
    });
    t.setResolution(pixelRatio());
    t.setOrigin(...(opts.origin ?? [0.5, 0.5]));
    return t;
  }

  /** Dark pill with text; `stroke` draws a hollow pill (§5.4 hollow badge). */
  private pill(x: number, y: number, str: string, opts: { color?: number; textColor?: string; hollow?: boolean; size?: number; lock?: boolean; bold?: boolean } = {}): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const t = this.text(0, 0, str, { size: opts.size ?? 11, color: opts.textColor ?? (opts.hollow ? hex(opts.color ?? this.pal.badgeBg) : this.pal.badgeText), bold: opts.bold ?? true });
    const w = Math.ceil(t.width) + 8, h = Math.ceil(t.height) + 2;
    const g = this.scene.add.graphics();
    // Square plates (mockup: hard corners everywhere).
    if (opts.hollow) { g.lineStyle(this.pal.strokeWidth, opts.color ?? 0xffffff, 1); g.fillStyle(this.pal.badgeBg, 0.8); g.fillRect(-w / 2, -h / 2, w, h); g.strokeRect(-w / 2 + 0.5, -h / 2 + 0.5, w - 1, h - 1); }
    else { g.fillStyle(opts.color ?? this.pal.badgeBg, 1); g.fillRect(-w / 2, -h / 2, w, h); g.lineStyle(1, 0x000000, 0.6); g.strokeRect(-w / 2 + 0.5, -h / 2 + 0.5, w - 1, h - 1); }
    c.add([g, t]);
    if (opts.lock) { const p = this.scene.add.graphics(); p.fillStyle(0xffffff, 1); p.fillCircle(w / 2 - 1, -h / 2 + 1, 2.5); p.lineStyle(1, 0x000000, 0.8); p.strokeCircle(w / 2 - 1, -h / 2 + 1, 2.5); c.add(p); }
    c.setSize(w, h);
    return c;
  }

  private dashed(g: Phaser.GameObjects.Graphics, x1: number, y1: number, x2: number, y2: number, dash = 5, gap = 4, phase = 0): void {
    const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy);
    if (len === 0) return;
    const ux = dx / len, uy = dy / len;
    let d = -(phase % (dash + gap));
    while (d < len) {
      const a = Math.max(0, d), b = Math.min(len, d + dash);
      if (b > a) g.lineBetween(x1 + ux * a, y1 + uy * a, x1 + ux * b, y1 + uy * b);
      d += dash + gap;
    }
  }

  private arrowHead(g: Phaser.GameObjects.Graphics, x: number, y: number, angle: number, size = 7): void {
    const a1 = angle + Math.PI * 0.8, a2 = angle - Math.PI * 0.8;
    g.fillTriangle(x, y, x + Math.cos(a1) * size, y + Math.sin(a1) * size, x + Math.cos(a2) * size, y + Math.sin(a2) * size);
  }

  private hatch(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, step = 5): void {
    for (let d = -h; d < w; d += step) {
      const x1 = Math.max(x, x + d), y1 = y + (x1 - (x + d));
      const x2 = Math.min(x + w, x + d + h), y2 = y + (x2 - (x + d));
      if (x2 > x1) g.lineBetween(x1, y1, x2, y2);
    }
  }

  private boundary(g: Phaser.GameObjects.Graphics, tiles: Tile[]): void {
    const set = new Set(tiles.map((t) => `${t.x},${t.y}`));
    for (const t of tiles) {
      const o = this.origin(t), p = this.px;
      if (!set.has(`${t.x},${t.y - 1}`)) g.lineBetween(o.x, o.y, o.x + p, o.y);
      if (!set.has(`${t.x + 1},${t.y}`)) g.lineBetween(o.x + p, o.y, o.x + p, o.y + p);
      if (!set.has(`${t.x},${t.y + 1}`)) g.lineBetween(o.x, o.y + p, o.x + p, o.y + p);
      if (!set.has(`${t.x - 1},${t.y}`)) g.lineBetween(o.x, o.y, o.x, o.y + p);
    }
  }

  /** `1`/`2`/`★` (a bonus action) badge with the hero's shape mark beside it (§5.2 badges). */
  private slotBadge(ref: IntentRef, style: Style, x: number, y: number, colour: number): Phaser.GameObjects.Container {
    const mark = SHAPE_MARKS[((this.states.get(ref.heroId)?.shapeMark ?? ref.heroId) - 1 + 12) % 12] ?? '●';
    const c = this.pill(x, y, `${slotMark(ref.slot)} ${mark}`, { color: colour, hollow: style.badge === 'hollow', lock: style.badge === 'lock', textColor: style.badge === 'hollow' ? hex(colour) : '#0b1020', size: 11 });
    return c;
  }

  private flag(f: ConflictFlag, x: number, y: number): Phaser.GameObjects.Container {
    const colour = f.severity === 'red' ? this.pal.red : this.pal.amber;
    const c = this.scene.add.container(x, y);
    const g = this.scene.add.graphics();
    g.lineStyle(1, colour, 1); g.lineBetween(0, 0, 0, -16);
    g.fillStyle(colour, 1); g.fillTriangle(0, -16, 12, -12, 0, -8);
    c.add([g, this.text(0, 3, f.severity === 'red' ? '!' : '?', { size: 10, color: hex(colour), bold: true, origin: [0.5, 0] })]);
    c.setData('reason', f.reason);
    return c;
  }

  // ---- effect renderers (§5.2) --------------------------------------------------------------

  private buildEffect(e: ProjectedEffect, style: Style, flags: ConflictFlag[]): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    let anchor: { x: number; y: number };
    switch (e.kind) {
      case 'move': anchor = this.buildMove(c, e, style); break;
      case 'attack': anchor = this.buildAttack(c, e, style); break;
      case 'area': anchor = this.buildArea(c, e, style); break;
      case 'support': anchor = this.buildSupport(c, e, style); break;
      case 'puzzle': anchor = this.buildPuzzle(c, e, style); break;
      case 'neutral': anchor = this.buildNeutral(c, e, style); break;
    }
    const colour = this.colourFor(e.kind);
    // Slots of one hero that share an anchor tile fan out vertically (1 at the top, 2, then B).
    const slotRow = e.id.slot === 1 ? 0 : e.id.slot === 2 ? 1 : 2;
    c.add(this.slotBadge(e.id, style, anchor.x, anchor.y + slotRow * 13, colour));
    flags.forEach((f, i) => { const at = f.at ? this.centre(f.at) : anchor; c.add(this.flag(f, at.x + 8 + i * 14, at.y - 4)); });
    if (style.struck) { const g = this.scene.add.graphics(); g.lineStyle(2, this.pal.red, 1); g.lineBetween(anchor.x - 14, anchor.y - 14, anchor.x + 14, anchor.y + 14); c.add(g); }
    c.setAlpha(style.alpha);
    return c;
  }

  private colourFor(kind: ProjectedEffect['kind']): number {
    switch (kind) {
      case 'move': return this.pal.move;
      case 'attack': return this.pal.attack;
      case 'area': return this.pal.area;
      case 'support': return this.pal.support;
      case 'puzzle': return this.pal.hint;
      case 'neutral': return this.pal.declaration;
    }
  }

  private buildMove(c: Phaser.GameObjects.Container, e: Extract<ProjectedEffect, { kind: 'move' }>, style: Style) {
    const g = this.scene.add.graphics();
    // Mockup 05: your plan in your player colour, dashed for a move (high-contrast keeps the palette's white).
    const seat = this.view.colourMode === 'highContrast' ? this.pal.move : this.seatOf(e.id);
    const width = this.pal.strokeWidth + (style.mine ? 1 : 0);
    // docs/79: a huge body's route runs through its footprint's centres and its ghost fills it
    const n = this.sizeOfUnit(this.heroUnit(e.id.heroId)?.unitId);
    const draw = (phase: number) => {
      g.clear();
      if (this.hidePathFor(e.id)) return;
      let amber = false;
      // the route from the hero's own square (docs/26: the sim's path starts one step out)
      const pts = e.from ? [e.from, ...e.path] : e.path;
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = this.mid(pts[i]!, n), b = this.mid(pts[i + 1]!, n);
        if (e.blocked && tileEq(pts[i]!, e.blocked)) amber = true;
        g.lineStyle(width, amber ? this.pal.amber : seat, 1);
        this.dashed(g, a.x, a.y, b.x, b.y, 6, 4, phase);
      }
      const last = e.path[e.path.length - 1];
      if (last) { const p = this.mid(last, n); g.fillStyle(amber ? this.pal.amber : seat, 1); g.fillRect(p.x - 2, p.y - 2, 4, 4); }
    };
    draw(this.view.dashPhase);
    c.add(g);
    c.setData('dash', g); c.setData('dashRedraw', draw);

    // Ghost silhouette at 40 % alpha using the unit's own frame, outline kept (§5.2).
    const unit = this.heroUnit(e.id.heroId);
    const ghostTile = e.blocked ? e.blocked : e.ghost;
    if (unit) {
      const { key, frame } = this.frameOf(unit);
      const p = this.pivot(ghostTile, n);
      const z = this.view.zoom * bodyScale(key, n);
      const oy = bodyOriginY(key);
      const outlineColour = e.blocked ? this.pal.amber : seat;
      // its square first: the ghost stands over it, as a figure stands over its ground (docs/145)
      const ring = this.scene.add.graphics();
      const o = this.origin(ghostTile), side = this.px * n;
      if (n > 1) { ring.fillStyle(outlineColour, 0.14); ring.fillRect(o.x, o.y, side, side); }
      ring.lineStyle(width, outlineColour, 0.95);
      if (style.ghost === 'dashed') { this.dashed(ring, o.x + 1, o.y + 1, o.x + side - 1, o.y + 1, 3, 3); this.dashed(ring, o.x + side - 1, o.y + 1, o.x + side - 1, o.y + side - 1, 3, 3); this.dashed(ring, o.x + side - 1, o.y + side - 1, o.x + 1, o.y + side - 1, 3, 3); this.dashed(ring, o.x + 1, o.y + side - 1, o.x + 1, o.y + 1, 3, 3); }
      else ring.strokeRect(o.x + 1, o.y + 1, side - 2, side - 2);
      c.add(ring);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        c.add(this.scene.add.image(p.x + dx, p.y + dy, key, frame).setOrigin(0.5, oy).setScale(z).setTint(outlineColour).setTintMode(Phaser.TintModes.FILL).setAlpha(0.6));
      }
      c.add(this.scene.add.image(p.x, p.y, key, frame).setOrigin(0.5, oy).setScale(z).setAlpha(0.4));
      if (style.ghost === 'pulse') this.scene.tweens.add({ targets: ring, alpha: { from: 1, to: 0.25 }, duration: 700, yoyo: true, repeat: -1 });
    }
    const first = e.path[0] ?? e.ghost;
    const a = this.origin(first);
    return { x: a.x + 12, y: a.y + 8 };
  }

  private buildAttack(c: Phaser.GameObjects.Container, e: Extract<ProjectedEffect, { kind: 'attack' }>, _style: Style) {
    const g = this.scene.add.graphics();
    // docs/81 SA2: from where the attacker will stand as it strikes (after its earlier slots), to
    // where the target stands then — never from the square the pulse found it on
    const fromN = this.sizeOfUnit(this.heroUnit(e.id.heroId)?.unitId), toN = this.sizeOfUnit(e.target);
    const from = this.mid(e.from, fromN), to = this.mid(e.to ?? this.endTile(e.target), toN);
    const ang = Math.atan2(to.y - from.y, to.x - from.x);
    const sx = from.x + Math.cos(ang) * this.px * 0.38 * fromN, sy = from.y + Math.sin(ang) * this.px * 0.38 * fromN;
    const ex = to.x - Math.cos(ang) * this.px * 0.38 * toN, ey = to.y - Math.sin(ang) * this.px * 0.38 * toN;
    // docs/81 SA6: a thick line with an arrowhead at the target, on a dark keyline so it reads on any ground
    const { width, head } = attackStroke(this.px, this.pal.strokeWidth);
    const bx = ex - Math.cos(ang) * head * 0.6, by = ey - Math.sin(ang) * head * 0.6;
    if (e.beam) { g.lineStyle(width + 6, this.pal.attack, 0.2); g.lineBetween(sx, sy, bx, by); }
    g.lineStyle(width + 2, 0x0b0f1a, 0.55); g.lineBetween(sx, sy, bx, by);
    g.fillStyle(0x0b0f1a, 0.55); this.arrowHead(g, ex + Math.cos(ang) * 1.5, ey + Math.sin(ang) * 1.5, ang, head + 2);
    g.lineStyle(width, this.pal.attack, 1);
    g.lineBetween(sx, sy, bx, by);
    g.fillStyle(this.pal.attack, 1); this.arrowHead(g, ex, ey, ang, head);
    c.add(g);
    const mx = (sx + ex) / 2, my = (sy + ey) / 2;
    // docs/103 RB4: a flanked blow, while the plans line up — a gold pincer on the foe's far side
    // (where the friend will stand) and the call-out over the number; it goes when they don't
    if (e.flank) {
      const pin = this.scene.add.graphics();
      const far = { x: to.x + Math.cos(ang) * this.px * 0.62 * toN, y: to.y + Math.sin(ang) * this.px * 0.62 * toN };
      const big = Math.max(head * 1.6, this.px * 0.32);
      pin.fillStyle(0x0b0f1a, 0.75); this.arrowHead(pin, far.x + Math.cos(ang) * 2, far.y + Math.sin(ang) * 2, ang + Math.PI, big + 3);
      pin.fillStyle(FLANK_GOLD, 1); this.arrowHead(pin, far.x, far.y, ang + Math.PI, big);
      c.add(pin);
      // over the foe's head, clear of the slot badges round the attacker
      const words = e.flank.kind === 'backstab' ? 'BACKSTAB ×2' : 'FLANKED +2';
      c.add(this.pill(to.x, to.y - this.px * 0.62 * toN - this.fontPx(13) / 2, words, { color: FLANK_GOLD, textColor: '#2a1a00', size: 13 }));
    }
    c.add(this.pill(mx, my, `−${e.damage}`, { color: this.pal.badgeBg, textColor: e.flank ? hex(FLANK_GOLD) : hex(this.pal.attack), size: 12 }));
    if (e.kill) c.add(this.text(to.x, to.y - this.px * 0.45 * toN, '☠', { size: 14, color: hex(this.pal.attack) }));
    // a blow that shoves (a Shield Bash, a Maul): the push from where the blow lands
    if (e.knockback) this.buildShove(c, e.target, e.to ?? this.endTile(e.target), e.knockback, []);
    return { x: from.x - this.px * 0.3, y: from.y - this.px * 0.35 };
  }

  private buildArea(c: Phaser.GameObjects.Container, e: Extract<ProjectedEffect, { kind: 'area' }>, _style: Style) {
    const g = this.scene.add.graphics();
    g.fillStyle(this.pal.area, 0.22);
    for (const t of e.tiles) { const o = this.origin(t); g.fillRect(o.x, o.y, this.px, this.px); }
    g.lineStyle(this.pal.strokeWidth, this.pal.area, 0.95);
    this.boundary(g, e.tiles);
    c.add(g);
    // Hazard symbol at the area's centre tile.
    const cx = e.tiles.reduce((s, t) => s + t.x, 0) / e.tiles.length, cy = e.tiles.reduce((s, t) => s + t.y, 0) / e.tiles.length;
    const centre = this.centre({ x: Math.round(cx), y: Math.round(cy) });
    if (e.tiles.length) {
      const star = this.scene.add.graphics();
      star.fillStyle(this.pal.area, 0.9);
      star.fillPoints(Array.from({ length: 16 }, (_, i) => { const r = i % 2 ? 4 : 10, a = (i / 16) * Math.PI * 2; return new Phaser.Math.Vector2(centre.x + Math.cos(a) * r, centre.y + Math.sin(a) * r); }), true);
      c.add(star);
    }
    for (const h of e.hits) this.buildHit(c, h);
    const first = e.tiles[0] ?? this.actsFrom(e);
    const o = this.origin(first);
    return { x: o.x + 12, y: o.y - 8 };
  }

  private buildHit(c: Phaser.GameObjects.Container, h: AreaHit): void {
    // where it stands as the blow lands: its end square is past this effect's own shove (docs/81 SA2)
    const t = h.at ?? this.endTile(h.target);
    const n = this.sizeOfUnit(h.target);
    // docs/79: a huge body's figures sit at its footprint's corner
    const o0 = this.origin(t), o = { x: o0.x + this.px * (n - 1), y: o0.y + this.px * (n - 1) };
    c.add(this.pill(o.x + this.px - 12, o.y + this.px - 8, `−${h.damage}`, { color: this.pal.badgeBg, textColor: hex(this.pal.area), size: 11 }));
    h.statuses.forEach((s, i) => c.add(this.pill(o.x + this.px - 12, o.y + this.px - 8 + 12 + i * 12, STATUS_ABBR[s.kind] ?? s.kind.slice(0, 2), { color: this.pal.area, textColor: '#0b1020', size: 9 })));
    if (h.kill) c.add(this.text(o.x + this.px / 2, o.y + 4, '☠', { size: 14, color: hex(this.pal.attack) }));
    if (h.knockback) this.buildShove(c, h.target, t, h.knockback, h.statuses);
  }

  /**
   * A shove: an arrow from where the blow lands (`t`) to where it pushes `target`, or a bar at the
   * square that stops it short; into the deep, the landing square is ringed in waves (docs/93 AQ8).
   */
  private buildShove(c: Phaser.GameObjects.Container, target: number, t: Tile, kb: { to: Tile; blockedAt?: Tile }, statuses: AreaHit['statuses']): void {
    const n = this.sizeOfUnit(target);
    const g = this.scene.add.graphics();
    const from = this.mid(t, n);
    if (kb.blockedAt) {
      const b = this.centre(kb.blockedAt);
      const ang = Math.atan2(b.y - from.y, b.x - from.x);
      const ex = from.x + Math.cos(ang) * this.px * 0.4, ey = from.y + Math.sin(ang) * this.px * 0.4;
      g.lineStyle(2, this.pal.amber, 1); g.lineBetween(from.x, from.y, ex, ey);
      g.lineBetween(ex + Math.cos(ang + Math.PI / 2) * 5, ey + Math.sin(ang + Math.PI / 2) * 5, ex - Math.cos(ang + Math.PI / 2) * 5, ey - Math.sin(ang + Math.PI / 2) * 5);
    } else {
      const to = this.mid(kb.to, n);
      const ang = Math.atan2(to.y - from.y, to.x - from.x);
      // docs/93 AQ8: the shove carries it over the edge into the deep — a blue arrow, the landing
      // square ringed in waves, and FLOUNDERS when it cannot swim there
      const board = this.board;
      const into = board ? shoveIntoWater(kb.to, this.states.get(target), statuses, (q) => waterAt(board, q)) : { deep: false, flounders: false };
      const col = into.deep ? DEEP_MARK : this.pal.declaration;
      g.lineStyle(into.deep ? 2 : 1, col, 1); g.lineBetween(from.x, from.y, to.x, to.y);
      g.fillStyle(col, 1); this.arrowHead(g, to.x, to.y, ang, into.deep ? 8 : 6);
      if (into.deep) {
        const o2 = this.origin(kb.to);
        g.lineStyle(2, DEEP_MARK, 0.95);
        this.dashed(g, o2.x + 2, o2.y + 2, o2.x + this.px * n - 2, o2.y + 2, 3, 3); this.dashed(g, o2.x + 2, o2.y + this.px * n - 2, o2.x + this.px * n - 2, o2.y + this.px * n - 2, 3, 3);
        this.dashed(g, o2.x + 2, o2.y + 2, o2.x + 2, o2.y + this.px * n - 2, 3, 3); this.dashed(g, o2.x + this.px * n - 2, o2.y + 2, o2.x + this.px * n - 2, o2.y + this.px * n - 2, 3, 3);
        // two little waves across the square
        for (const f of [0.42, 0.62]) {
          g.lineStyle(1, DEEP_MARK, 0.9); g.beginPath();
          for (let i = 0; i <= 8; i++) { const x = o2.x + this.px * n * (0.2 + 0.6 * i / 8), y = o2.y + this.px * n * f + Math.sin(i * Math.PI / 2) * 1.5; if (i) g.lineTo(x, y); else g.moveTo(x, y); }
          g.strokePath();
        }
        if (into.flounders) c.add(this.pill(o2.x + (this.px * n) / 2, o2.y - 6, 'FLOUNDERS', { color: DEEP_MARK, textColor: '#06182a', size: 9 }));
      }
    }
    c.add(g);
  }

  private buildSupport(c: Phaser.GameObjects.Container, e: Extract<ProjectedEffect, { kind: 'support' }>, _style: Style) {
    const g = this.scene.add.graphics();
    const col = this.pal.support;
    const tiles: Tile[] = e.onTile ? [e.onTile] : e.beneficiaries.map((_, i) => this.helpedAt(e, i));
    // the caster where it stands as it acts (docs/81 SA2)
    const heroTile = this.actsFrom(e);
    // docs/79: a huge body's glyph and link sit on its whole footprint, not its corner square
    const casterN = this.sizeOfUnit(this.heroUnit(e.id.heroId)?.unitId);
    const sides = e.onTile ? [1] : e.beneficiaries.map((b) => this.sizeOfUnit(b));
    tiles.forEach((t, i) => {
      const n = sides[i] ?? 1;
      const p = this.mid(t, n);
      const x = p.x + this.px * 0.3 * n, y = p.y - this.px * 0.3 * n, s = 6;
      g.lineStyle(this.pal.strokeWidth + 0.5, col, 1);
      switch (e.glyph) {
        case 'shield': g.fillStyle(col, 0.25); g.fillPoints([new Phaser.Math.Vector2(x - s, y - s), new Phaser.Math.Vector2(x + s, y - s), new Phaser.Math.Vector2(x + s, y + 1), new Phaser.Math.Vector2(x, y + s + 2), new Phaser.Math.Vector2(x - s, y + 1)], true); g.strokePoints([new Phaser.Math.Vector2(x - s, y - s), new Phaser.Math.Vector2(x + s, y - s), new Phaser.Math.Vector2(x + s, y + 1), new Phaser.Math.Vector2(x, y + s + 2), new Phaser.Math.Vector2(x - s, y + 1)], true, true); break;
        case 'heal': g.fillStyle(col, 1); g.fillRect(x - 2, y - s, 4, s * 2); g.fillRect(x - s, y - 2, s * 2, 4); break;
        case 'ward': g.fillStyle(col, 0.25); g.fillPoints([new Phaser.Math.Vector2(x, y - s - 1), new Phaser.Math.Vector2(x + s + 1, y), new Phaser.Math.Vector2(x, y + s + 1), new Phaser.Math.Vector2(x - s - 1, y)], true); g.strokePoints([new Phaser.Math.Vector2(x, y - s - 1), new Phaser.Math.Vector2(x + s + 1, y), new Phaser.Math.Vector2(x, y + s + 1), new Phaser.Math.Vector2(x - s - 1, y)], true, true); break;
        case 'guard': g.strokePoints([new Phaser.Math.Vector2(x - s, y + s), new Phaser.Math.Vector2(x - s, y - s), new Phaser.Math.Vector2(x + s, y - s), new Phaser.Math.Vector2(x + s, y + s)], false, false); g.lineBetween(x - s + 3, y + 2, x + s - 3, y + 2); break;
        case 'interact': g.strokeCircle(x, y, s); g.fillStyle(col, 1); g.fillCircle(x, y, 2); for (let i = 0; i < 4; i++) { const a = i * Math.PI / 2 + Math.PI / 4; g.lineBetween(x + Math.cos(a) * s, y + Math.sin(a) * s, x + Math.cos(a) * (s + 3), y + Math.sin(a) * (s + 3)); } break;
      }
      if (e.amount !== undefined) c.add(this.pill(x, y + s + 10, `+${e.amount}`, { color: this.pal.badgeBg, textColor: hex(col), size: 11 }));
      // Caster → beneficiary link when they differ.
      if (!tileEq(t, heroTile)) { const a = this.mid(heroTile, casterN); g.lineStyle(this.pal.strokeWidth, col, 0.5); this.dashed(g, a.x, a.y, p.x, p.y, 3, 4); }
    });
    c.add(g);
    const o = this.origin(tiles[0] ?? heroTile);
    return { x: o.x + 12, y: o.y + 8 };
  }

  private buildPuzzle(c: Phaser.GameObjects.Container, e: Extract<ProjectedEffect, { kind: 'puzzle' }>, _style: Style) {
    const g = this.scene.add.graphics();
    const col = this.pal.hint;
    switch (e.glyph) {
      case 'flood': g.fillStyle(this.pal.move, 0.3); for (const t of e.tiles) { const o = this.origin(t); g.fillRect(o.x, o.y, this.px, this.px); } break;
      case 'wire': g.lineStyle(2, e.lit ? this.pal.area : col, 1); for (let i = 0; i + 1 < e.tiles.length; i++) { const a = this.centre(e.tiles[i]!), b = this.centre(e.tiles[i + 1]!); g.lineBetween(a.x, a.y, b.x, b.y); } break;
      case 'push': case 'turn': g.lineStyle(this.pal.strokeWidth, col, 1); for (const t of e.tiles) { const o = this.origin(t); this.dashed(g, o.x + 2, o.y + 2, o.x + this.px - 2, o.y + 2, 3, 3); this.dashed(g, o.x + 2, o.y + this.px - 2, o.x + this.px - 2, o.y + this.px - 2, 3, 3); } if (e.glyph === 'turn' && e.tiles[0]) { const p = this.centre(e.tiles[0]); g.strokeCircle(p.x, p.y, this.px * 0.3); } break;
      case 'countdown': if (e.tiles[0]) { const p = this.centre(e.tiles[0]); c.add(this.pill(p.x, p.y, `${e.value ?? ''}`, { color: col, textColor: '#0b1020' })); } break;
    }
    c.add(g);
    const o = this.origin(e.tiles[0] ?? this.actsFrom(e));
    return { x: o.x + 12, y: o.y + 8 };
  }

  private buildNeutral(c: Phaser.GameObjects.Container, e: Extract<ProjectedEffect, { kind: 'neutral' }>, _style: Style) {
    // docs/81 SA2: a Hold, Defend or Search is marked where the hero stands as it acts
    const t = this.actsFrom(e);
    // docs/79: on a huge body, at its footprint's top-right, as on one square
    const n = this.sizeOfUnit(this.heroUnit(e.id.heroId)?.unitId);
    const p = this.mid(t, n);
    const g = this.scene.add.graphics();
    g.lineStyle(this.pal.strokeWidth + 0.5, this.pal.declaration, 1);
    const x = p.x + this.px * 0.3 * n, y = p.y - this.px * 0.3 * n;
    g.strokePoints([new Phaser.Math.Vector2(x - 5, y - 4), new Phaser.Math.Vector2(x, y + 1), new Phaser.Math.Vector2(x + 5, y - 4)], false, false);
    g.strokePoints([new Phaser.Math.Vector2(x - 5, y + 1), new Phaser.Math.Vector2(x, y + 6), new Phaser.Math.Vector2(x + 5, y + 1)], false, false);
    c.add(g);
    // Substituted holds (server-filled at commit or lock) get one compact chip per slot row, beside the badge, never a stack.
    if (e.substituted) { const row = e.id.slot === 1 ? 0 : e.id.slot === 2 ? 1 : 2; const o0 = this.origin(t); c.add(this.pill(o0.x + 12 + 26, o0.y + 8 + row * 13, 'hold', { color: this.pal.amber, textColor: '#0b1020', size: 8 })); }
    const o = this.origin(t);
    return { x: o.x + 12, y: o.y + 8 };
  }

  private buildOrdering(o: OrderingNote, then: ProjectedEffect, i: number): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const t = this.effectOrigin(then);
    const p = this.centre(t);
    const chip = this.pill(p.x, p.y + this.px * 0.42 + i * 0, '①→②', { color: this.pal.badgeBg, textColor: this.pal.text, size: 10 });
    chip.setData('why', o.why);
    c.add(chip);
    return c;
  }

  private effectOrigin(e: ProjectedEffect): Tile {
    switch (e.kind) {
      case 'move': return e.path[0] ?? e.ghost;
      case 'attack': return e.from;
      case 'area': return e.tiles[0] ?? this.actsFrom(e);
      case 'support': return e.onTile ?? (e.beneficiaries[0] !== undefined ? this.helpedAt(e, 0) : this.actsFrom(e));
      case 'puzzle': return e.tiles[0] ?? this.actsFrom(e);
      case 'neutral': return this.actsFrom(e);
    }
  }

  private frameOf(unit: UnitState): { key: string; frame: string } {
    return unitFrame(this.manifest, unit, (k, f) => this.scene.textures.exists(k) && this.scene.textures.get(k).has(f));
  }

  private heroUnit(heroId: number): UnitState | undefined {
    for (const u of this.states.values()) if (u.heroId === heroId) return u;
    return undefined;
  }

  // ---- HUD (§5.3) ----------------------------------------------------------------------------

  /**
   * Unit plate (mockup 03): HP as a row of ticks under the unit, one per point — green, amber below
   * half, red at a quarter; foes always red. Ticks this plan would take are hatched red; the figure
   * itself is in the target preview (HUD), not on the board.
   */
  private buildUnitHud(u: ProjectedUnit, targeted: boolean): Phaser.GameObjects.Container {
    // docs/46 T2: nothing planned has happened yet — the bar shows the HP the unit has now and the
    // statuses it carries now; a planned blow is marked on its target, not taken off its bar
    const c = this.scene.add.container(0, 0);
    const o = this.origin(u.start);
    const foe = this.states.get(u.unitId)?.faction === 'enemy';
    const n = Math.max(1, u.hpMax);
    // docs/79: a huge body's plate spans its footprint, under its bottom row
    const side = this.px * this.sizeOfUnit(u.unitId);
    const w = side - 8, h = Math.max(3, Math.round(this.view.zoom * 1.5));
    const x0 = o.x + 4, y = o.y + side - h - 1;
    const step = w / n, tw = Math.max(1, Math.floor(step) - (step >= 3 ? 1 : 0));
    const now = Math.max(0, u.hpStart);
    const ratio = now / n;
    const on = foe ? this.pal.hpLost : ratio <= 0.25 ? this.pal.red : ratio < 0.5 ? this.pal.amber : this.pal.hpKeep;
    const g = this.scene.add.graphics();
    g.fillStyle(0x05070d, 0.85); g.fillRect(x0 - 1, y - 1, w + 2, h + 2);
    for (let i = 0; i < n; i++) {
      const tx = x0 + Math.round(i * step);
      g.fillStyle(i < now ? on : this.pal.hpBg, 1); g.fillRect(tx, y, tw, h);
    }
    c.add(g);
    if (targeted) { const r = this.scene.add.graphics(); r.lineStyle(2, this.pal.attack, 0.9); r.strokeRect(o.x + 2, o.y + 2, side - 4, side - 4); c.add(r); }
    return c;
  }

  /** Mockup 05: the chosen action's reach as tinted tiles with a thin edge per tile. */
  private buildReach(tiles: Tile[], tone: keyof typeof REACH): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    const [fill, edge] = REACH[tone];
    // Tiles under a unit keep only the edge, so the overlay never washes a sprite out.
    const occupied = this.occupiedSet();
    for (const t of tiles) {
      const o = this.origin(t);
      if (!occupied.has(`${t.x},${t.y}`)) { g.fillStyle(fill, 0.3); g.fillRect(o.x, o.y, this.px, this.px); }
      const filled = !occupied.has(`${t.x},${t.y}`);
      g.lineStyle(filled ? 1 : 2, edge, filled ? 0.5 : 0.95); g.strokeRect(o.x + 1.5, o.y + 1.5, this.px - 3, this.px - 3);
    }
    g.lineStyle(1, edge, 0.85); this.boundary(g, tiles);
    c.add(g);
    return c;
  }

  /**
   * docs/48 AR3: an action's range. Every square it reaches is washed in the action's colour, the
   * squares the eye cannot reach (a wall in the line, or past what the sky lets the hero see) only
   * hatched, and the whole range outlined; where the hero will stand when it acts carries a ring
   * when that is not where it stands now. On top, each unit (or square) the action may aim at
   * gets bright corner brackets — the answer to "whom can I hit from there?".
   */
  private buildAim(aim: AimRange, targets: Tile[], tone: keyof typeof REACH, quiet: boolean, units: boolean): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    const [fill, edge] = REACH[tone];
    const p = this.px;
    const k = quiet ? 0.6 : 1;
    const occupied = this.occupiedSet();
    const clear = aim.tiles.filter((t) => t.why === 'Clear').map((t) => t.tile);
    const unseen = aim.tiles.filter((t) => t.why !== 'Clear').map((t) => t.tile);
    for (const t of clear) {
      if (occupied.has(`${t.x},${t.y}`)) continue;
      const o = this.origin(t);
      g.fillStyle(fill, 0.2 * k); g.fillRect(o.x, o.y, p, p);
    }
    // out of sight: a stipple, no wash — in range, but not a square to aim at (dots, so rain streaks never read as it)
    g.fillStyle(edge, 0.5 * k);
    const dot = Math.max(1, Math.round(p / 16));
    for (const t of unseen) {
      const o = this.origin(t);
      for (const fx of [0.25, 0.75]) for (const fy of [0.25, 0.75]) g.fillRect(Math.round(o.x + p * fx - dot / 2), Math.round(o.y + p * fy - dot / 2), dot, dot);
    }
    g.lineStyle(1, edge, 0.35 * k); this.boundary(g, aim.tiles.map((t) => t.tile));
    g.lineStyle(this.pal.strokeWidth + 1, edge, 0.9 * k); this.boundary(g, clear);
    // the square the hero acts from, when an earlier slot moves it there
    const hero = this.units.get(aim.unit);
    if (hero && !tileEq(hero.start, aim.from)) {
      const o = this.origin(aim.from);
      g.lineStyle(2, edge, 0.95 * k); g.strokeCircle(o.x + p / 2, o.y + p / 2, p * 0.42);
      g.fillStyle(edge, 0.25 * k); g.fillCircle(o.x + p / 2, o.y + p / 2, p * 0.42);
    }
    c.add(g);
    // the targets: light corner brackets just outside the square, outlined dark, so they stand out from the wash under them
    const mark = parseInt(this.pal.text.slice(1), 16);
    const b = this.scene.add.graphics();
    const arm = Math.max(4, p * 0.28), out = 2;
    for (const t of targets) {
      const o = this.origin(t);
      if (!units) { b.fillStyle(fill, 0.22 * k); b.fillRect(o.x, o.y, p, p); continue; }
      // docs/79: a huge target is bracketed round its whole footprint
      const n = sizeOf(t as Tile & { size?: number });
      this.brackets(b, t, n, mark, out, arm * Math.min(2, n), [[5, this.pal.badgeBg, 0.45 * k], [2.5, mark, k]]);
    }
    c.add(b);
    return c;
  }

  /**
   * The field for a Move (docs/26 F8): every square the hero can stop on, washed deeper the cheaper
   * it is to reach. Zones of control are not drawn (docs/46 T7): where the reach ends shows them.
   */
  private buildField(f: Survey, quiet = false): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    if (quiet) c.setAlpha(0.6);
    const [fill, edge] = REACH.move;
    const occupied = this.occupiedSet();
    const budget = Math.max(1, f.budget);
    const n = this.sizeOfUnit(f.unit);
    // docs/79: a huge body's stops are origins — wash every square some stop's footprint covers, at the cheapest
    const stops = n > 1 ? bodyStops(f.reach.filter((r) => r.stand), n) : f.reach.filter((r) => r.stand);
    for (const r of stops) {
      const o = this.origin(r.tile);
      const free = !occupied.has(`${r.tile.x},${r.tile.y}`);
      // docs/139 WD5: a never-seen square is walked into on hope: a fainter wash, corner ticks
      if (neverSeen(this.fog, r.tile)) {
        g.fillStyle(fill, 0.1); g.fillRect(o.x, o.y, this.px, this.px);
        this.brackets(g, r.tile, 1, edge, -2, Math.max(3, Math.round(this.px / 6)), [[1, edge, 0.55]]);
        continue;
      }
      if (free) { g.fillStyle(fill, 0.16 + 0.2 * (1 - r.cost / budget)); g.fillRect(o.x, o.y, this.px, this.px); }
      g.lineStyle(1, edge, 0.4); g.strokeRect(o.x + 1.5, o.y + 1.5, this.px - 3, this.px - 3);
    }
    g.lineStyle(1, edge, 0.85); this.boundary(g, stops.map((r) => r.tile));
    // docs/46 T7: zones of control are not drawn — the reach already stops where they hold the hero
    c.add(g);
    return c;
  }

  /**
   * The A* route to the cursor (docs/26): the line the hero will walk, a pip per step (amber where
   * the step is heavy going), and a chip with what the walk costs — or, off the reach, what the
   * ground under the cursor is and why it cannot be reached.
   */
  /** docs/149 MP: a turn tag on every square a walk under way reaches, numbered by the pulse it is reached in (1 is this turn). */
  private buildWalkTags(r: RouteTag): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    r.legs.forEach((leg, k) => leg.forEach((t) => {
      const p = this.centre(t);
      c.add(this.pill(p.x, p.y, String(k + 1), { size: 10, hollow: k > 0 }));
    }));
    return c;
  }

  private buildRoute(f: Survey, hovered: Tile, bare = false, isOrigin = false): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    // docs/79: a huge body walks to the origin whose footprint covers the hovered square (as the
    // click plans it); a smart click's `walkTo` is that origin already
    const n = this.sizeOfUnit(f.unit);
    const stands = new Map(f.reach.filter((r) => r.stand).map((r) => [`${r.tile.x},${r.tile.y}`, r.cost]));
    const cursor = n > 1 && !isOrigin ? originFor(hovered, n, (t) => stands.get(`${t.x},${t.y}`)) ?? hovered : hovered;
    const route = fieldRoute(f, cursor);
    const p = n > 1 && route ? this.mid(cursor, n) : this.centre(hovered);
    if (route) {
      const g = this.scene.add.graphics();
      const pts = [f.from, ...route.tiles];
      const w = this.pal.strokeWidth + 1;
      g.lineStyle(w + 2, 0x0b0f1a, 0.55);
      for (let i = 0; i + 1 < pts.length; i++) { const a = this.mid(pts[i]!, n), b = this.mid(pts[i + 1]!, n); g.lineBetween(a.x, a.y, b.x, b.y); }
      g.lineStyle(w, REACH.move[1], 1);
      for (let i = 0; i + 1 < pts.length; i++) { const a = this.mid(pts[i]!, n), b = this.mid(pts[i + 1]!, n); g.lineBetween(a.x, a.y, b.x, b.y); }
      if (n > 1) {
        // the ghost of the whole body where the walk ends
        const o = this.origin(cursor), side = this.px * n;
        g.fillStyle(REACH.move[0], 0.18); g.fillRect(o.x, o.y, side, side);
        g.lineStyle(2, REACH.move[1], 1); g.strokeRect(o.x + 1, o.y + 1, side - 2, side - 2);
      }
      route.tiles.forEach((t, i) => {
        const q = this.mid(t, n);
        const heavy = route.costs[i]! > f.pace;
        const r = heavy ? 3 : 2;
        g.fillStyle(0x0b0f1a, 0.9); g.fillRect(q.x - r - 1, q.y - r - 1, 2 * r + 2, 2 * r + 2);
        g.fillStyle(heavy ? this.pal.amber : 0xeef0f6, 1); g.fillRect(q.x - r, q.y - r, 2 * r, 2 * r);
      });
      c.add(g);
    }
    // docs/139 WD5: of a never-seen square only the walk is told, and nothing when it cannot go
    const dark = neverSeen(this.fog, cursor);
    const note = bare || (dark && !route) ? undefined : fieldTileNote(f, cursor, route, dark);
    if (note) c.add(this.chip(p.x, p.y + this.px * 0.5 * (route ? n : 1) + 9, note, route ? (route.entangled ? 'harm' : 'guard') : 'grey'));
    if (route && bare) {
      // the square the hero closes to before acting: a stand mark
      const o = this.origin(cursor), g = this.scene.add.graphics();
      g.lineStyle(2, REACH.move[1], 1); g.strokeRect(o.x + 2, o.y + 2, this.px * n - 4, this.px * n - 4);
      c.add(g);
    }
    return c;
  }

  /**
   * docs/33 E1/E2: what a click here would do — a reticle on the foe it would strike and a label
   * over the cursor ("Basic Attack Ratch", "Close in · Strike Hobb", "Select Teo"), or why not.
   */
  private buildSuggest(cursor: Tile, sug: Suggestion | undefined, note: string | undefined): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const p = this.centre(cursor);
    const y = p.y - this.px * 0.5 - 10;
    if (note) { c.add(this.chip(p.x, y, note, 'harm')); return c; }
    if (!sug) return c;
    const target = sug.kind !== 'select' ? sug.target : undefined;
    if (target !== undefined) {
      const t = this.startTile(target), g = this.scene.add.graphics();
      const col = sug.kind === 'plan' ? this.pal.attack : 0x8a90a6;
      // four corner brackets round the whole body (docs/79), a dark keyline under the colour
      this.brackets(g, t, this.sizeOfUnit(target), col, -1);
      c.add(g);
    }
    // the action is the chip's top line, what is on the square under it (alone over a walk: the route's own chip says what the walk costs)
    const about = sug.kind === 'plan' ? sug.about : undefined;
    if (sug.kind === 'plan' && sug.tone === 'move') { if (about) c.add(this.chip(p.x, y, about, 'grey')); return c; }
    const words = sug.kind === 'select' ? sug.label : `${sug.short ?? sug.label}${sug.kind === 'plan' && sug.hit ? ` · ${sug.hit.damage} dmg${sug.hit.drops ? ' · drops' : ''}` : ''}`;
    c.add(this.chip(p.x, y, words, sug.kind === 'blocked' ? 'grey' : sug.kind === 'select' ? 'guard' : sug.tone === 'attack' ? 'harm' : 'guard', about));
    return c;
  }

  /**
   * Enemy intent chip (mockup 04) over a foe during planning: what it will do, to whom, with the
   * sim's exact figure — red-tinted for harm, navy for guard/hold, gold for the leader's order.
   */
  private chip(x: number, y: number, str: string, kind: 'harm' | 'guard' | 'area' | 'order' | 'grey', sub?: string): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const style = { harm: [0x4a1416, 0xc0393b, '#ffe2df'], guard: [0x16203a, 0x5a6d9a, '#dfe6f5'], area: [0x3a2452, 0x9a72d0, '#efe2ff'], order: [0xf4cf78, 0xf4cf78, '#1a1406'], grey: [0x24283a, 0x6b7590, '#c9cedb'] }[kind] as [number, number, string];
    const t = this.text(0, 0, str, { size: 11, color: style[2], bold: true, origin: [0, 0.5] });
    // `sub`: a plain line under the bold one (the action, over what is on the square)
    const under = sub ? this.text(0, 0, sub, { size: 11, color: style[2], origin: [0, 0.5] }) : undefined;
    const icon = kind === 'order' || kind === 'grey' ? 0 : 9;
    const w = Math.ceil(Math.max(t.width + icon, under?.width ?? 0)) + 8, h = Math.ceil(t.height + (under?.height ?? 0)) + 1;
    const g = this.scene.add.graphics();
    g.fillStyle(0x05070d, 0.9); g.fillRect(-w / 2 - 1, -h / 2 - 1, w + 2, h + 2);
    g.fillStyle(style[0], 1); g.fillRect(-w / 2, -h / 2, w, h);
    g.lineStyle(1, style[1], 1); g.strokeRect(-w / 2 + 0.5, -h / 2 + 0.5, w - 1, h - 1);
    const iy = under ? -(h - t.height) / 2 : 0, ix = -w / 2 + 4;
    // the extra line grows upward, off the square the chip names
    if (under) { under.setPosition(-w / 2 + 4, (h - under.height) / 2); t.setY(iy); c.setY(y - under.height / 2); }
    g.fillStyle(style[1] === 0x5a6d9a ? 0x9fb3e0 : 0xff8a7a, 1);
    if (kind === 'harm') { for (let k = 0; k < 5; k++) g.fillRect(ix + k, iy + 2 - k, 1, 1); g.fillRect(ix, iy + 1, 3, 1); g.fillRect(ix + 1, iy, 1, 3); }
    else if (kind === 'guard') { g.fillRect(ix, iy - 3, 5, 4); g.fillRect(ix + 1, iy + 1, 3, 1); g.fillRect(ix + 2, iy + 2, 1, 1); }
    else if (kind === 'area') { g.fillStyle(0xd7b8ff, 1); g.fillRect(ix + 1, iy - 3, 3, 1); g.fillRect(ix, iy - 2, 5, 3); g.fillRect(ix + 1, iy + 1, 3, 1); }
    t.setX(-w / 2 + 4 + icon);
    c.add(under ? [g, t, under] : [g, t]);
    c.setSize(w, h);
    c.setData('chip', true);
    return c;
  }

  private buildSelection(t: Tile, n = 1): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    const o = this.origin(t);
    g.lineStyle(2, 0xffd166, 1); g.strokeRect(o.x + 1, o.y + 1, this.px * n - 2, this.px * n - 2);
    c.add(g);
    this.scene.tweens.add({ targets: g, alpha: { from: 1, to: 0.5 }, duration: 600, yoyo: true, repeat: -1 });
    return c;
  }

  private buildCursor(t: Tile, n = 1): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    const o = this.origin(t);
    g.lineStyle(1, 0xffffff, 0.9); g.strokeRect(o.x + 0.5, o.y + 0.5, this.px * n - 1, this.px * n - 1);
    c.add(g);
    return c;
  }

  private dangerTiles(decls: EnemyDeclaration[]): Tile[] {
    const out = new Map<string, Tile>();
    for (const d of decls) {
      if (d.held) continue;
      if (d.region) for (const t of d.region) out.set(`${t.x},${t.y}`, t);
      if (d.aim.kind === 'tile') out.set(`${d.aim.tile.x},${d.aim.tile.y}`, d.aim.tile);
      if (d.aim.kind === 'region') for (const t of d.aim.tiles) out.set(`${t.x},${t.y}`, t);
      if (d.aim.kind === 'unit' && d.projected.some(([, n]) => n > 0)) for (const t of tilesAt(this.endTile(d.aim.target), this.sizeOfUnit(d.aim.target))) out.set(`${t.x},${t.y}`, t);
    }
    return [...out.values()];
  }

  private buildDanger(tiles: Tile[]): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    g.lineStyle(1, this.pal.attack, 0.35);
    for (const t of tiles) { const o = this.origin(t); this.hatch(g, o.x, o.y, this.px, this.px, 6); }
    c.add(g);
    return c;
  }

  // ---- declarations (grey layer, §4.2 / §5.2) -------------------------------------------------

  private buildDeclaration(d: EnemyDeclaration): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    const grey = this.pal.declaration;
    const start = this.startTile(d.actor);
    const standTile = d.ghost ?? start;
    const unit = this.states.get(d.actor);
    const n = sizeOf(unit ?? {});
    if (d.ghost && !tileEq(d.ghost, start)) {
      // docs/39 FW6: the walk it means to take, square by square (the sim's route), not a beeline
      const route = d.path && d.path.length && tileEq(d.path[d.path.length - 1]!, d.ghost) ? [start, ...d.path] : [start, d.ghost];
      g.lineStyle(this.pal.strokeWidth, grey, 0.9);
      for (let i = 0; i + 1 < route.length; i++) { const a = this.mid(route[i]!, n), b = this.mid(route[i + 1]!, n); this.dashed(g, a.x, a.y, b.x, b.y, 4, 4); }
      if (unit) {
        const { key, frame } = this.frameOf(unit);
        const p = this.pivot(d.ghost, n);
        c.add(this.scene.add.image(p.x, p.y, key, frame).setOrigin(0.5, bodyOriginY(key)).setScale(this.view.zoom * bodyScale(key, n)).setTint(grey).setTintMode(Phaser.TintModes.FILL).setAlpha(0.45));
      }
    }
    if (!d.held && d.aim.kind === 'unit') {
      const toN = this.sizeOfUnit(d.aim.target);
      const from = this.mid(standTile, n), to = this.mid(this.endTile(d.aim.target), toN);
      const ang = Math.atan2(to.y - from.y, to.x - from.x);
      const r = this.px * 0.38;
      const sx = from.x + Math.cos(ang) * r * n, sy = from.y + Math.sin(ang) * r * n, ex = to.x - Math.cos(ang) * r * toN, ey = to.y - Math.sin(ang) * r * toN;
      g.lineStyle(this.pal.strokeWidth, grey, 0.9); g.lineBetween(sx, sy, ex, ey);
      g.fillStyle(grey, 0.9); this.arrowHead(g, ex, ey, ang, 6);
    }
    if (d.region || (d.aim.kind === 'region')) {
      const tiles = d.region ?? (d.aim.kind === 'region' ? d.aim.tiles : []);
      g.fillStyle(grey, 0.15); for (const t of tiles) { const o = this.origin(t); g.fillRect(o.x, o.y, this.px, this.px); }
      g.lineStyle(this.pal.strokeWidth, grey, 0.8); this.boundary(g, tiles);
      const t0 = tiles[0]; if (t0) { const p = this.centre(t0); c.add(this.text(p.x, p.y, '◬', { size: 14, color: hex(grey) })); }
    }
    c.add(g);
    // Intent chips over the foe where it stands now (mockup 04): the sim's exact figures only.
    const o = this.origin(start);
    const name = (u: number) => this.states.get(u)?.name ?? `#${u}`;
    const sum = (u?: number) => d.projected.filter(([t]) => u === undefined || t === u).reduce((s, [, n]) => s + n, 0);
    const chips: Array<{ text: string; kind: 'harm' | 'guard' | 'area' | 'order' | 'grey' }> = [];
    if (d.held) chips.push({ text: 'Hold', kind: 'guard' });
    else if (d.aim.kind === 'unit') { const n = sum(d.aim.target); chips.push({ text: `${d.actionName} ${name(d.aim.target)}${n > 0 ? ` ${n}` : ''}`, kind: n > 0 ? 'harm' : 'guard' }); }
    else if (d.aim.kind === 'region' || d.region) { const n = sum(); chips.push({ text: `${d.actionName}${n > 0 ? ` ${n}` : ''}`, kind: 'area' }); }
    else if (d.aim.kind === 'tile') chips.push({ text: d.actionName, kind: 'harm' });
    else chips.push({ text: d.stance ? d.stance.replace('_', ' ') : d.actionName, kind: 'guard' });
    if (d.retargeted) chips.push({ text: `↷ ${name(d.retargeted[1])}`, kind: 'grey' });
    if (d.leader && d.standingOrder) chips.unshift({ text: `Order: ${d.standingOrder}`, kind: 'order' });
    chips.forEach((ch, i) => { const node = this.chip(0, 0, ch.text, ch.kind); node.setPosition(Math.round(o.x + (this.px * n) / 2), Math.round(o.y - 2 - (chips.length - 1 - i) * 15 - node.height / 2)); c.add(node); });
    c.setAlpha(this.view.filter === 'mine' ? 0.6 : 1);
    return c;
  }
}

